import { FC } from 'react';
import { useConfigContext } from '@fe-core';
import { useIntl } from 'react-intl';
import { cn } from '@fe-learner-common';

const VerticalSeparator = () => (
  <span className="lf:mx-2 lf:h-3 lf:w-px lf:bg-neutral-700" />
);

type FooterProps = {
  className?: string;
  isLoading?: boolean;
};

const Skeleton = () => (
  <div className="lf:flex lf:items-center lf:justify-center lf:space-x-2">
    <div className="lf:h-4 lf:w-[300px] lf:animate-pulse lf:rounded-sm lf:bg-neutral-200" />
    <VerticalSeparator />
    <div className="lf:h-4 lf:w-[100px] lf:animate-pulse lf:rounded-sm lf:bg-neutral-200" />
    <VerticalSeparator />
    <div className="lf:h-4 lf:w-[100px] lf:animate-pulse lf:rounded-sm lf:bg-neutral-200" />
    <VerticalSeparator />
    <div className="lf:h-4 lf:w-[170px] lf:animate-pulse lf:rounded-sm lf:bg-neutral-200" />
  </div>
);

export const BxFooter: FC<FooterProps> = ({ className, isLoading }) => {
  const { config } = useConfigContext();
  const intl = useIntl();
  const realmConfig = config?.realmConfig as Record<string, string>;
  const privacyPolicyUrl = realmConfig['cocosite.privacypolicy.url'];
  const tosUrl = realmConfig['cocosite.tos.url'];

  return (
    <footer
      className={cn(
        'lf:flex lf:flex-wrap lf:items-center lf:justify-center lf:whitespace-nowrap lf:border-t-1 lf:border-neutral-200 lf:p-5 lf:text-body-sm',
        className,
      )}
    >
      {isLoading ? (
        <Skeleton />
      ) : (
        <>
          <p>
            {intl.formatMessage(
              {
                id: 'footer.company',
                defaultMessage: '© {year} Blanchard. All rights reserved',
              },
              { year: new Date().getFullYear() },
            )}
          </p>
          <VerticalSeparator />
          {privacyPolicyUrl && (
            <>
              <a href={privacyPolicyUrl} target="_blank" rel="noopener noreferrer">
                {intl.formatMessage({
                  id: 'footer.privacy.policy',
                  defaultMessage: 'Privacy Policy',
                })}
              </a>
              <VerticalSeparator />
            </>
          )}
          {tosUrl && (
            <>
              <a href={tosUrl} target="_blank" rel="noopener noreferrer">
                {intl.formatMessage({
                  id: 'footer.terms.of.service',
                  defaultMessage: 'Terms & Conditions',
                })}
              </a>
              <VerticalSeparator />
            </>
          )}
          <a
            href="https://learnifier.com"
            target="_blank"
            rel="noopener noreferrer"
          >
            {intl.formatMessage({
              id: 'footer.powered.by.learnifier',
              defaultMessage: 'Powered by Learnifier',
            })}
          </a>
        </>
      )}
    </footer>
  );
};
